// @ts-check
/** @typedef {import('../types/api').EmailTemplate} EmailTemplate */
/** @typedef {import('../types/api').EmailLog} EmailLog */
/** @typedef {import('../types/api').EmailPreview} EmailPreview */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminEmailTemplateService = {
  /**
   * @returns {Promise<EmailTemplate[]>}
   */
  getAllTemplates: async () => {
    const data = await api.get('/api/admin/email-templates/');
    assertArray(data, 'adminEmailTemplateService.getAllTemplates');
    return data;
  },

  /**
   * @param {string} templateId
   * @returns {Promise<EmailTemplate>}
   */
  getTemplate: async (templateId) => {
    const data = await api.get(`/api/admin/email-templates/${templateId}`);
    assertShape(data, ['id'], 'adminEmailTemplateService.getTemplate');
    return data;
  },

  /**
   * @param {Object} templateData
   * @returns {Promise<EmailTemplate>}
   */
  createTemplate: async (templateData) => {
    return api.post('/api/admin/email-templates/', templateData);
  },

  /**
   * @param {string} templateId
   * @param {Object} templateData
   * @returns {Promise<EmailTemplate>}
   */
  updateTemplate: async (templateId, templateData) => {
    return api.put(`/api/admin/email-templates/${templateId}`, templateData);
  },

  /**
   * @param {string} templateId
   * @returns {Promise<void>}
   */
  deleteTemplate: async (templateId) => {
    return api.del(`/api/admin/email-templates/${templateId}`);
  },

  /**
   * @param {string} templateId
   * @param {Object} variables
   * @returns {Promise<EmailPreview>}
   */
  previewTemplate: async (templateId, variables) => {
    const data = await api.post(`/api/admin/email-templates/${templateId}/preview`, variables);
    assertShape(data, ['subject', 'html_body'], 'adminEmailTemplateService.previewTemplate');
    return data;
  },

  /**
   * Preview unsaved editor content — no template needs to exist in the DB yet.
   * @param {{ subject: string, html_body: string, text_body?: string, variables: Object }} draft
   * @returns {Promise<EmailPreview>}
   */
  previewDraft: async (draft) => {
    const data = await api.post('/api/admin/email-templates/preview-draft', draft);
    assertShape(data, ['subject', 'html_body'], 'adminEmailTemplateService.previewDraft');
    return data;
  },

  /**
   * @param {number} [limit=50]
   * @param {number} [skip=0]
   * @returns {Promise<EmailLog[]>}
   */
  getSentEmailLogs: async (limit = 50, skip = 0) => {
    const data = await api.get(`/api/admin/email-templates/logs/?limit=${limit}&skip=${skip}`);
    assertArray(data, 'adminEmailTemplateService.getSentEmailLogs');
    return data;
  },
};

export default adminEmailTemplateService;
